import React, { useEffect, useState } from "react";
import {
  BackButton,
  ThinTextField,
  ThinDropdown,
  ThinDropdownWithSearch,
  AddButton,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const EditRGC = () => {
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [allDistricts, setAllDistricts] = useState([]);
  const [generatedCode, setGeneratedCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const generateCodeFromName = (name) => {
    if (name && name.length >= 2) {
      return name.substring(0, 2).toUpperCase();
    }
    return "";
  };
  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const [rgcResponse, districtsResponse] = await Promise.all([
          fetchData(
            `rgcs?rgc_id=${id}`,
            "GET",
            null,
            {},
            {},
            navigate,
            "/rgcs",
            dispatch
          ),
          fetchData(
            "districts",
            "GET",
            null,
            {},
            {},
            navigate,
            "/districts",
            dispatch
          ),
        ]);

        if (rgcResponse.data.length > 0) {
          const rgcData = rgcResponse.data[0];
          formik.setValues({
            rgc_name: rgcData.rgc_name,
            district_id: rgcData.district_id,
            status: rgcData.status,
          });
          // Set the existing code for display
          setGeneratedCode(rgcData.rgc_code);
        }

        const allDists = districtsResponse.data || [];
        setAllDistricts(allDists);
        const activeDistricts = allDists.filter((d) => d.status === 1);
        setDistricts(activeDistricts);
        setLoader(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
        setLoader(false);
      }
    };
    fetchDataAsync();
  }, [id]);

  // Function to handle status change with validation
  const handleStatusChange = (selectedStatus) => {
    // Check if trying to activate RGC
    if (selectedStatus == 1) {
      // Check if district is selected and active
      if (formik.values.district_id) {
        const selectedDistrict = allDistricts.find(
          (d) => d.district_id == formik.values.district_id
        );
        if (selectedDistrict && selectedDistrict.status === 0) {
          toast.error("Cannot activate RGC. First activate related District.");
          return; // Don't update the status
        }
      }
    }

    // Update the status if validation passes
    formik.setFieldValue("status", selectedStatus);
  };

  const formik = useFormik({
    initialValues: {
      rgc_name: "",
      district_id: "",
      status: 1,
    },
    validationSchema: Yup.object({
      rgc_name: Yup.string()
        .required("RGC Name is required")
        .matches(
          /^[a-zA-Z\s]+$/,
          "RGC Name cannot contain Special Characters or Number"
        )
        .min(3, "RGC Name must be at least 3 characters long")
        .max(50, "RGC Name must be at most 50 characters long"),
      district_id: Yup.string().required("District is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Additional validation: if activating RGC, check if selected district is active
        if (values.status == 1) {
          const selectedDistrict = allDistricts.find(
            (d) => d.district_id == values.district_id
          );
          if (selectedDistrict && selectedDistrict.status === 0) {
            toast.error(
              "Cannot activate RGC. First activate related District."
            );
            setIsSubmitting(false);
            return;
          }
        }

        await fetchData(
          "rgcs",
          "PUT",
          { ...values, rgc_id: id },
          {},
          {},
          navigate,
          "/rgcs",
          dispatch
        );
        toast.success("RGC updated successfully");
        navigate("/rgcs");
      } catch (error) {
        console.error("Update failed:", error);
        toast.error(
          error.message || error.response?.data?.message || "Update failed"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit RGC
        </h1>
        <BackButton text="Back" to="/rgcs" />
      </div>
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit RGC
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4 my-4">
            <ThinTextField
              label="RGC Name"
              name="rgc_name"
              value={formik.values.rgc_name}
              onChange={(e) => {
                formik.handleChange(e);
                const code = generateCodeFromName(e.target.value);
                setGeneratedCode(code);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.rgc_name && formik.errors.rgc_name}
              helperText={formik.touched.rgc_name && formik.errors.rgc_name}
            />
            <div>
              <ThinDropdownWithSearch
                label="District"
                options={districts.map((d) => ({
                  value: d.district_id || d.District_Id,
                  label: d.district_name || d.District_Name,
                }))}
                value={formik.values.district_id}
                onChange={(value) => formik.setFieldValue("district_id", value)}
                error={formik.touched.district_id && formik.errors.district_id}
                helperText={
                  formik.touched.district_id && formik.errors.district_id
                }
              />
              {districts.length === 0 && (
                <p className="text-sm text-red-500 mt-1">
                  No active districts available. Please activate a district
                  first.
                </p>
              )}
            </div>
            <ThinDropdown
              label="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(e) => handleStatusChange(e.target.value)}
            />
          </div>
          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={
                isSubmitting ||
                (districts.length === 0 && formik.values.status == 1)
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRGC;
